<template>
  <div class="c-search__input-container">
    <div
      v-if="isLoading"
      class="c-loading__container"
    >
      <SVGIcon class="loading-icon" name="loading" />
    </div>
    <template v-else>
      <form class="c-search__input-form" @submit.prevent="onSearchSubmit">
        <input
          ref="inputElement"
          v-model="inputTextContent"
          class="c-search__input"
          type="text"
          name="search"
          :placeholder="t('search')"
          @focus="focusInput"
          @focusout="focusOutInput"
          @input="onSearchInput"
        >
      </form>
      <div
        :class="{ 'c-search--visible': hasText && inputTextContent.length <= inputCharsMax}"
        class="c-search__cancel-container"
        @click="deleteInputText"
      >
        <SVGIcon class="c-search__cancel-icon" name="cancel-square" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import SVGIcon from '@/components/SVGIcon.vue'
// import { logDevOnly } from '@/utils/logs'
import { useI18n } from '@/utils/i18n'

// logDevOnly()

const inputElement = ref<HTMLElement>()
const inputTextContent = ref('')
const inputCharsMax = 25

const isLoading = ref(true)
const isFocused = ref(false)

const hasText = ref(false)

function focusInput () {
  isFocused.value = true

  inputElement.value!.parentElement!.classList.add('c-search--focused')
}

function focusOutInput () {
  if (inputTextContent.value.length <= 0) {
    isFocused.value = false

    inputElement.value!.parentElement!.classList.remove('c-search--focused')
  }
}

function onSearchInput () {
  if (inputTextContent.value.length > 0) {
    hasText.value = true
  } else {
    hasText.value = false
  }
}

function deleteInputText () {
  inputTextContent.value = ''

  hasText.value = false

  const headerContainerEl = ref(document.getElementsByClassName('c-header__container')[0] as HTMLElement)
  headerContainerEl.value.style.paddingRight = '4rem'
}

function onSearchSubmit () {
  // get query text
  const queryText = inputTextContent.value

  // add query text to /search url searchParams
  const searchUrl = ref(new URL(window.location.origin + '/search/'))
  searchUrl.value.searchParams.append('q', queryText)
  console.log('SEARCH URL: ', searchUrl.value.href)

  if (window.location.pathname !== '/search/') {
    // go to url if not in search page
    window.location.href = searchUrl.value.href
  }
}

onMounted(async () => {
  // add text to search bar if url has query
  const urlSearchParams = new URL(window.location.href).searchParams
  const queryContent = urlSearchParams.get('q')
  if (queryContent) {
    inputTextContent.value = queryContent
  }
  isLoading.value = false
})

const { t } = useI18n({
  messages: {
    en: {
      search: 'Search'
    },
    fr: {
      search: 'Rechercher'
    }
  }
})
</script>

<style scoped lang="scss">
@use "sass-rem" as rem;
@use "@/sass/settings";
@use "@/sass/helpers/mq";

.c-search__input-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
}

.c-search__input-form {
  appearance: none;
  border: none;
  width: 100%;
}

.c-search__input {
  appearance: none;
  border: none;
  width: 100%;
  min-width: 3.5rem;
  min-height: rem.convert(40px);
  background-image: url("/static/icons/search.svg");
  background-repeat: no-repeat;
  background-position: left 20px top 10px;
  text-indent: 3rem;
  font-size: 1.2rem;
}

.c-search__input:focus {
  outline: none;
  background-image: url("/static/icons/search-blue.svg");
}

.c-search__cancel-container {
  display: none;
}

.c-search__cancel-container:hover {
  cursor: pointer;
}

@include mq.mq($breakpoints: settings.$mq-breakpoints, $until: medium) {
  .c-search__input {
    min-width: 3rem;
    min-height: 2.5rem;
    background-position: left 10px top 10px;
    text-indent: rem.convert(40px);
  }
}

@include mq.mq($breakpoints: settings.$mq-breakpoints, $from: large) {
  .c-search__cancel-container {
    align-items: center;
    border-left: 1px solid #000;
    margin-right: 1rem;
    padding-left: 0.5rem;
    position: absolute;
    right: 0;
  }

  .c-search--visible {
    display: flex;
  }
}
</style>
